/**
 * Боковая панель работы на `/work-status` (задача 114 §2.2).
 *
 * **Non-modal панель уровня оболочки** (`patterns.md §Боковая панель у
 * реестра`): визуальный язык дока — 460px, во всю высоту рабочей области, шапка
 * с разделителем — но без Radix-Dialog и без оверлея. Реестр слева остаётся
 * видимым И кликабельным: работы переключаются строкой, не закрывая панель.
 * Ни `<Inspector>` (панель свойств у канваса — своя, более мелкая шкала), ни
 * `<Sheet>` (модальный налог) тут не подходят; контейнер даёт `AppShell` слотом
 * `aside`, потому что «во всю высоту» достижимо только сестрой `<main>`.
 *
 * **Автосохранение.** Кнопки «Сохранить» нет: смена статуса, прогноза и отметка
 * точки уходят сразу, комментарий — по blur. Повторная отправка того же
 * значения безопасна — сервер различает правку по изменению значения (§1.3),
 * поэтому ни ретрай оптимистики, ни гонка двух вкладок не засоряют журнал.
 *
 * Компонент surface-агностичен (всё приходит пропсами, медиа-запросов внутри
 * нет) — §3.1 переиспользует его на мобильном экране без форка.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Plus, Square, SquareCheck, Trash2, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/templates/ConfirmDialog";
import { DateField } from "@/components/ui/date-field";
import {
  apiErrorFormIssue,
  FormFieldError,
  type FormValidationIssue,
} from "@/components/ui/form-validation";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { SegmentedStatus } from "@/components/ui/segmented-status";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { formatDate, formatDateTime, formatRelativeDateTime } from "@/lib/datetime";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";
import { useWorkCheckpointMutations, useWorkDetail, usePatchWork } from "@/hooks/useWorkStatus";
import type { ProjectWorkStatus, WorkForecast, WorkPair, WorkStatusDetail } from "@/api/workStatus";

const STATUSES: ProjectWorkStatus[] = ["planned", "in_progress", "paused", "completed"];
const FORECASTS: WorkForecast[] = ["gt2w", "w2", "w1", "d3", "d1"];

/** Сегодня в ISO — дефолт даты завершения при закрытии работы. */
function todayIso(): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Поверхность панели: правая колонка десктопа или fullscreen-экран телефона. */
export type WorkPanelSurface = "panel" | "mobile";

export interface WorkPanelProps {
  pair: WorkPair;
  /** Контейнер и навигация: `mobile` — fullscreen + «назад», история отдельным экраном. */
  surface?: WorkPanelSurface;
  /** Юнит нужен admin: без него сервер не знает сцену и отвечает 422. */
  unitId?: number | null;
  includeChildren?: boolean;
  locale: string;
  /** Закрыть панель. На мобильном (§3.1) это «назад» к списку. */
  onClose: () => void;
}

export function WorkPanel({
  pair,
  surface = "panel",
  unitId = null,
  includeChildren = false,
  locale,
  onClose,
}: WorkPanelProps): React.ReactElement {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useWorkDetail(pair, unitId, includeChildren);
  const patch = usePatchWork(pair);
  const { add, toggle, remove, comment } = useWorkCheckpointMutations(pair);
  // Ошибка валидации привязана к ПОЛЮ: дат теперь две (завершение и срок), и
  // 422 обязана встать у той, которую правили, а не «где-то в панели».
  const [fieldError, setFieldError] = useState<{
    field: "completedAt" | "dueDate";
    issue: FormValidationIssue;
  } | null>(null);
  // Экран журнала на телефоне (4d) — локальный state, отдельного роута нет
  // (решение плана §3.1: `/work-status` адаптивен, навигация внутри).
  const [historyOpen, setHistoryOpen] = useState(false);

  if (isLoading || (data == null && !isError)) {
    return (
      <PanelShell surface={surface} onClose={onClose} title={t("common.loading")}>
        <span className="text-muted-foreground">{t("common.loading")}</span>
      </PanelShell>
    );
  }
  if (data == null) {
    return (
      <PanelShell surface={surface} onClose={onClose} title={t("common.apiError.fallback")}>
        <ApiErrorNotice error={error} fallbackMessage={t("common.apiError.fallback")} />
      </PanelShell>
    );
  }

  const workId = data.project_work_id ?? null;
  const isCompleted = data.status === "completed";
  const events = data.events ?? [];

  if (surface === "mobile" && historyOpen) {
    return (
      <PanelShell
        surface={surface}
        onClose={() => setHistoryOpen(false)}
        title={t("workStatus.panel.history")}
      >
        <HistorySection detail={data} locale={locale} hideTitle />
      </PanelShell>
    );
  }

  const applyPatch = (
    body: Parameters<typeof patch.mutate>[0],
    errorField: "completedAt" | "dueDate" = "completedAt",
  ): void => {
    setFieldError(null);
    patch.mutate(body, {
      onError: (err) => {
        setFieldError({
          field: errorField,
          issue: apiErrorFormIssue(err, {
            id: `work-panel-${errorField}-server-error`,
            fallbackMessage: t("common.apiError.fallback"),
          }),
        });
      },
    });
  };

  return (
    <PanelShell surface={surface} onClose={onClose} title={data.work_type_name}>
      {/* Контекст работы: где она и чья. Название вида работ здесь не
          повторяем — оно уже в шапке панели. */}
      <header className="flex flex-col gap-1">
        <span className="text-[var(--fg-2)]">
          <span className="font-mono tabular-nums">{data.project_code}</span> · {data.project_name}
        </span>
        <span className="text-xs text-[var(--fg-3)]">
          {(data.performers ?? []).length > 0
            ? (data.performers ?? []).map((performer) => performer.full_name).join(", ")
            : t("workStatus.panel.noPerformer")}
          {data.updated_at != null && (
            <>
              {" · "}
              {t("workStatus.panel.updatedAt", {
                when: formatRelativeDateTime(data.updated_at, {
                  todayLabel: t("common.relativeTime.today"),
                  locale,
                }),
              })}
            </>
          )}
        </span>
      </header>

      {/* Просрочка — оверлей РЯДОМ со статусом, а не вместо него (канон задачи
          103): работа остаётся «в работе», просто опоздала. Живёт в СТРОКЕ
          ЛЕЙБЛА: в слоте значения она отодвигала бы вниз сам селект, то есть
          управление, а в реестре она стоит именно рядом со статусом. */}
      <Field
        label={t("workStatus.panel.status")}
        labelAside={
          data.overdue_days != null ? (
            <Badge tone="danger" emphasis="solid" data-testid="work-panel-overdue">
              {t("workStatus.overdueBadge", { count: data.overdue_days })}
            </Badge>
          ) : null
        }
      >
        <Select
          value={data.status}
          data-testid="work-panel-status"
          placeholder={t("workStatus.panel.status")}
          onChange={(value) => applyPatch({ status: value as ProjectWorkStatus })}
          options={STATUSES.map((s) => ({ value: s, label: t(`workStatus.status.${s}`) }))}
        />
      </Field>

      {/* Дата завершения видна, только пока работа закрыта; после reopen
          исчезает вместе со смыслом (решение владельца 20.07.2026). */}
      {isCompleted && (
        <Field
          label={t("workStatus.panel.completedAt")}
          issue={fieldError?.field === "completedAt" ? fieldError.issue : null}
        >
          <DateField
            value={data.completed_at ?? todayIso()}
            label={t("workStatus.panel.completedAt")}
            data-testid="work-panel-completed-at"
            required
            onChange={(value) => {
              if (value && value !== data.completed_at) {
                applyPatch({ status: "completed", completed_at: value });
              }
            }}
          />
          {/* Последствие правки должно быть очевидно ДО неё, а не после,
              поэтому предупреждение идёт читаемой ролью подписей, а не
              `--fg-4` (это роль disabled/placeholder, контраст ~3:1). */}
          <span className="text-muted-foreground mt-1 block text-xs">
            {t("workStatus.panel.completedAtHint")}
          </span>
        </Field>
      )}

      {/* Работы может ещё не быть — любое из этих действий её заведёт (§1.3).
          Ничего не гейтим по `workId`: мёртвых кликов в панели нет. */}
      <CheckpointsSection
        detail={data}
        locale={locale}
        onToggle={(itemId, catalogItemId, isDone) =>
          toggle.mutate({ itemId, catalogItemId, isDone })
        }
        onRemove={(itemId) => remove.mutateAsync({ itemId }).then(() => undefined)}
        removePending={remove.isPending}
        onAdd={(title) => add.mutate({ workId, title })}
      />

      {/* Срок — абсолютная дата «к какому дню нужно». Прогноз её не заменяет:
          он относительный, а просрочка считается только от даты. Поле
          необязательное — руководитель ставит срок там, где он нужен.
          Значение позже будет приходить из Ганта; панель читает его из
          `data.due_date` и не знает, кто его проставил, поэтому смена
          источника этот код не затронет. */}
      <Field
        label={t("workStatus.panel.dueDate")}
        issue={fieldError?.field === "dueDate" ? fieldError.issue : null}
      >
        <DateField
          value={data.due_date ?? null}
          label={t("workStatus.panel.dueDate")}
          data-testid="work-panel-due-date"
          onChange={(value) => {
            if (value !== data.due_date) {
              applyPatch(
                value === null ? { due_date_cleared: true } : { due_date: value },
                "dueDate",
              );
            }
          }}
        />
      </Field>

      <Field label={t("workStatus.panel.forecast")}>
        <SegmentedStatus<WorkForecast>
          value={data.forecast ?? null}
          // Панель — `flex-col`, поэтому контрол растягиваем ЯВНО: иначе
          // родитель тянет рамку, а сегменты жмутся влево (грабля записана в
          // `patterns.md §StatusSegmentedControl`).
          stretch
          data-testid="work-panel-forecast"
          ariaLabel={t("workStatus.panel.forecast")}
          // Повторный клик по выбранному сегменту снимает прогноз: поставить
          // его было можно, а вернуть в «—» — нет, хотя поле необязательное.
          onChange={(value) =>
            applyPatch(value === data.forecast ? { forecast_cleared: true } : { forecast: value })
          }
          options={FORECASTS.map((f) => ({
            value: f,
            label: t(`workStatus.forecast.${f}`),
            // Прогноз — не статус: тон один на все сегменты, смысл несёт
            // выбранная позиция, а не цвет.
            tone: "tone-neutral",
          }))}
        />
      </Field>

      <CommentField onCommit={(memo) => comment.mutate({ workId, memo })} />

      {/* На телефоне журнал — ОТДЕЛЬНЫЙ экран (4d макапа): в одну прокрутку с
          полями он уводит управление далеко вниз. На десктопе места хватает,
          и он остаётся частью панели. */}
      {surface === "mobile" ? (
        <button
          type="button"
          data-testid="work-panel-history-open"
          onClick={() => setHistoryOpen(true)}
          className="mt-auto flex items-center justify-between gap-2 border-t border-[var(--rule-line)] pt-4 text-left"
          style={{ minHeight: "var(--entry-tap-tertiary)" }}
        >
          <span className="text-sm text-[var(--fg-2)]">
            {t("workStatus.panel.history")}
            {events.length > 0 && (
              <span className="ml-1 font-mono tabular-nums text-[var(--fg-3)]">
                {events.length}
              </span>
            )}
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-[var(--fg-3)]" aria-hidden />
        </button>
      ) : (
        <HistorySection detail={data} locale={locale} />
      )}
    </PanelShell>
  );
}

/**
 * Каркас панели: шапка с названием работы и крестиком + прокручиваемое тело.
 * Контракт шапки и отступов взят у дока (`EntryDock` / `TimesheetDayEditor`) —
 * это та же «правая рабочая панель», и расходиться с ней в типографике не за
 * что. Отдельной подписи-категории над названием нет: название работы идёт
 * первой строкой и в подписи «Работа» не нуждается.
 */
/**
 * Каркас панели. Тело одно на обе поверхности, **различается только контейнер**
 * (прецедент `TimesheetDayEditor`, задача 79): на десктопе — правая колонка
 * оболочки, на телефоне — fullscreen-экран поверх реестра (M6, задача 71 §6.2)
 * с «назад» вместо крестика. Второго UI не заводим.
 *
 * `data-entry-surface="mobile"` включает density-токены `--entry-tap-*`
 * (задача 77 §2.1) — крупные тач-таргеты на телефоне.
 */
function PanelShell({
  title,
  surface,
  onClose,
  children,
}: {
  title: string;
  surface: WorkPanelSurface;
  onClose: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation();
  const mobile = surface === "mobile";
  return (
    <aside
      data-testid="work-panel"
      data-surface={surface}
      data-entry-surface={mobile ? "mobile" : undefined}
      aria-label={title}
      className={
        mobile
          ? "fixed inset-0 z-40 flex flex-col bg-popover text-popover-foreground"
          : "flex w-[460px] shrink-0 flex-col overflow-hidden border-l border-[var(--rule-line)] bg-popover text-popover-foreground"
      }
    >
      <div className="flex items-center justify-between gap-2 border-b border-[var(--rule-line)] px-4 py-3">
        {mobile && (
          <button
            type="button"
            data-testid="work-panel-back"
            aria-label={t("common.back")}
            onClick={onClose}
            className="-ml-1 inline-flex shrink-0 items-center justify-center rounded-[var(--radius-button)] text-[var(--fg-2)]"
            style={{
              minWidth: "var(--entry-tap-tertiary)",
              minHeight: "var(--entry-tap-tertiary)",
            }}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
        )}
        <span className="flex-1 truncate text-base font-semibold text-[var(--fg-1)]">{title}</span>
        {!mobile && (
          <Button
            size="icon"
            variant="ghost"
            className="shrink-0"
            data-testid="work-panel-close"
            aria-label={t("common.close")}
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-4 text-sm">{children}</div>
    </aside>
  );
}

function Field({
  label,
  labelAside,
  issue,
  children,
}: {
  label: string;
  /** Метка в строке лейбла (напр. просрочка): рядом с названием поля, а не в
   *  слоте значения — иначе она отодвигает сам контрол вниз. */
  labelAside?: React.ReactNode;
  issue?: FormValidationIssue | null;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-xs text-[var(--fg-3)]">{label}</span>
        {labelAside}
      </div>
      {children}
      {issue != null && <FormFieldError issue={issue} />}
    </div>
  );
}

/**
 * Отметка контрольной точки. Роль `checkbox` на кнопке, а не нативный input:
 * визуал должен совпадать с колонкой точек в реестре (`Square`/`SquareCheck`),
 * а нативный чекбокс в нашей системе не стилизован и выбивается из панели.
 */
function CheckpointToggle({
  title,
  isDone,
  testId,
  onToggle,
  children,
}: {
  title: string;
  isDone: boolean;
  testId: string;
  onToggle: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  const Icon = isDone ? SquareCheck : Square;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={isDone}
      aria-label={title}
      data-testid={testId}
      onClick={onToggle}
      // Хит-область — вся строка (иконка + подпись), а не 14×14 у значка.
      // Кольцо фокуса своё, из токенов: UA-кольцо в 3px спорит с плотностью
      // панели.
      className="-mx-1 flex flex-1 items-start gap-2 rounded-[var(--radius-button)] px-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--fg-3)]"
    >
      <Icon
        aria-hidden="true"
        // Цвет неотмеченной — как в колонке точек реестра: одно состояние
        // показываем одинаково на обеих поверхностях.
        className={`mt-0.5 h-4 w-4 shrink-0 ${
          isDone ? "text-[var(--tone-success-fg)]" : "text-[var(--fg-4)]"
        }`}
      />
      <span className="flex-1">{children}</span>
    </button>
  );
}

function CheckpointsSection({
  detail,
  locale,
  onToggle,
  onRemove,
  removePending,
  onAdd,
}: {
  detail: WorkStatusDetail;
  locale: string;
  onToggle: (itemId: number | null, catalogItemId: number | null, isDone: boolean) => void;
  onRemove: (itemId: number) => Promise<void>;
  removePending: boolean;
  onAdd: (title: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  useReleaseDirtyState(draft !== "");
  // Точка, для которой открыт confirm. Удаление деструктивно и необратимо —
  // мутация живёт в `onConfirm`, а не в `onClick` корзинки
  // (`patterns.md §Подтверждение деструктивных действий`, прецедент 101 §1.3).
  const [pendingRemoval, setPendingRemoval] = useState<{ id: number; title: string } | null>(null);
  const items = detail.checkpoints ?? [];
  const done = items.filter((c) => c.is_done).length;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs text-[var(--fg-3)]">
        {t("workStatus.panel.checkpoints")}
        {items.length > 0 && (
          <span className="ml-1 font-mono tabular-nums">
            {done}/{items.length}
          </span>
        )}
      </span>
      {items.length === 0 && (
        <span className="text-[var(--fg-3)]">{t("workStatus.panel.noCheckpoints")}</span>
      )}
      {/* Пункты плотнее соседних секций на шаг шкалы (gap-2 → gap-1.5): это
          один список, а не набор независимых полей. */}
      <ul className="flex flex-col gap-1.5">
        {items.map((cp, i) => (
          <li key={cp.id ?? `preview-${i}`} className="flex items-start gap-2">
            {/* Отметка — те же `Square`/`SquareCheck`, что и в колонке точек
                реестра: панель и таблица говорят про одно и то же состояние,
                значит и выглядеть оно должно одинаково. Нативный
                `<input type=checkbox>` здесь был чужим — своего примитива
                чекбокса в системе нет, и заводить его ради одного места не
                стали. */}
            {/* Иконка и подпись — ОДИН контрол, а не значок с текстом рядом:
                кликом по названию отметить точку естественно, а 14×14 у одной
                иконки — самая мелкая цель панели (и компонент уедет на телефон,
                §3.1). Заодно у кнопки появляется осмысленное имя для
                скринридера — само название точки. */}
            <CheckpointToggle
              title={cp.title}
              isDone={cp.is_done}
              testId={`work-panel-checkpoint-${i}`}
              // У превью каталога своего id ещё нет — отдаём `catalog_item_id`,
              // по нему сервер заведёт работу и поставит отметку одним запросом.
              onToggle={() => onToggle(cp.id ?? null, cp.catalog_item_id ?? null, !cp.is_done)}
            >
              {cp.title}
              {cp.is_done && cp.done_at != null && (
                <span className="ml-1 text-xs text-[var(--fg-4)]">
                  {formatDate(cp.done_at.slice(0, 10), locale)}
                  {cp.done_by_name != null && ` · ${cp.done_by_name}`}
                </span>
              )}
            </CheckpointToggle>
            {cp.id != null && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    // Удаление — второстепенное действие строки: приглушено в
                    // покое и возвращает заметность под курсором и с
                    // клавиатуры. Статикой `--fg-4` читалась бы disabled (это
                    // его роль), но с возвратом на hover/focus двусмысленности
                    // нет — кнопка тихая, пока на неё не идут.
                    //
                    // Габарит `h-5` (17.5px при root 14px) вместо icon-дефолта
                    // `size-8` (28px): кнопка была самым высоким элементом
                    // строки и раздувала её, из-за чего список читался
                    // разреженным при уже маленьком зазоре (замер: строка
                    // 28 → 17.5px, шаг 33.3 → 22.8px).
                    //
                    // Выравнивание держится совпадением оптических центров, а
                    // не `items-center` на строке: у галки `mt-0.5` (1.75px) +
                    // иконка `h-4` (14px) → центр 8.75px; у корзинки 17.5px без
                    // отступа → те же 8.75px. `items-start` на `<li>` при этом
                    // сохранён намеренно — при переносе длинной подписи на две
                    // строки центрирование увело бы обе иконки вниз.
                    className="h-5 w-5 min-h-0 shrink-0 p-0 text-[var(--fg-4)] hover:text-[var(--fg-2)] focus-visible:text-[var(--fg-2)]"
                    aria-label={t("workStatus.panel.removeCheckpoint")}
                    data-testid={`work-panel-checkpoint-remove-${i}`}
                    onClick={() => setPendingRemoval({ id: cp.id as number, title: cp.title })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("workStatus.panel.removeCheckpoint")}</TooltipContent>
              </Tooltip>
            )}
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2">
        <Input
          value={draft}
          data-testid="work-panel-checkpoint-new"
          placeholder={t("workStatus.panel.addCheckpointPlaceholder")}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim() !== "") {
              onAdd(draft.trim());
              setDraft("");
            }
          }}
        />
        <Button
          size="icon"
          variant="outline"
          disabled={draft.trim() === ""}
          aria-label={t("workStatus.panel.addCheckpoint")}
          data-testid="work-panel-checkpoint-add"
          onClick={() => {
            onAdd(draft.trim());
            setDraft("");
          }}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <ConfirmDialog
        open={pendingRemoval != null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoval(null);
        }}
        destructive
        testId="work-panel-checkpoint-remove-confirm"
        title={t("workStatus.panel.removeCheckpointConfirmTitle")}
        // Название в вопросе: в списке точки различаются только им, и без
        // него подтверждать нечего — «удалить точку?» одинаково для всех.
        body={t("workStatus.panel.removeCheckpointConfirmBody", {
          title: pendingRemoval?.title ?? "",
        })}
        submitLabel={t("common.delete")}
        pending={removePending}
        onConfirm={async () => {
          if (pendingRemoval == null) return;
          try {
            await onRemove(pendingRemoval.id);
            setPendingRemoval(null);
          } catch (error) {
            showApiErrorToast(error, { fallbackMessage: t("common.apiError.fallback") });
          }
        }}
      />
    </div>
  );
}

/** Комментарий уходит в журнал по blur — отдельной кнопки «Сохранить» нет. */
function CommentField({ onCommit }: { onCommit: (memo: string) => void }): React.ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  useReleaseDirtyState(draft !== "");
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs text-[var(--fg-3)]">{t("workStatus.panel.comment")}</span>
      <Textarea
        rows={3}
        value={draft}
        data-testid="work-panel-comment"
        placeholder={t("workStatus.panel.commentPlaceholder")}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const memo = draft.trim();
          if (memo !== "") {
            onCommit(memo);
            setDraft("");
          }
        }}
      />
    </div>
  );
}

function HistorySection({
  detail,
  locale,
  hideTitle = false,
}: {
  detail: WorkStatusDetail;
  locale: string;
  // На отдельном мобильном экране журнала шапка панели уже озаглавливает экран
  // «История» — внутренний подзаголовок дублировал бы её. На десктопе журнал —
  // секция внутри панели рядом с полями, поэтому подзаголовок нужен.
  hideTitle?: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const events = detail.events ?? [];
  return (
    // Без `mt-auto`: панель теперь во всю высоту, и прижатый к низу журнал
    // отрывался бы от полей огромной пустотой. Читается сверху вниз подряд.
    <div className="flex flex-col gap-2 border-t border-[var(--rule-line)] pt-4">
      {!hideTitle && (
        <span className="text-xs text-[var(--fg-3)]">{t("workStatus.panel.history")}</span>
      )}
      {events.length === 0 ? (
        <span className="text-[var(--fg-3)]">{t("workStatus.panel.noHistory")}</span>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {events.map((e) => (
            <li key={e.id} className="flex flex-col gap-0.5">
              <span className="flex items-baseline gap-1.5">
                {e.occurred_at != null && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-xs text-[var(--fg-4)] whitespace-nowrap">
                        {formatRelativeDateTime(e.occurred_at, {
                          todayLabel: t("common.relativeTime.today"),
                          locale,
                        })}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{formatDateTime(e.occurred_at, locale)}</TooltipContent>
                  </Tooltip>
                )}
                <span className="flex-1">{describeEvent(e, t, locale)}</span>
              </span>
              {/* У `checkpoint` название точки уже внутри фразы, у `comment`
                  memo и есть сам текст — цитата в обоих случаях дубль. */}
              {e.memo != null && e.kind !== "comment" && e.kind !== "checkpoint" && (
                <span className="text-[var(--fg-2)] italic">
                  {t("workStatus.panel.event.memoQuote", { memo: e.memo })}
                </span>
              )}
              {e.actor_name != null && (
                <span className="text-xs text-[var(--fg-4)]">{e.actor_name}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Человекочитаемая строка события журнала. Сырые значения (`in_progress`,
 * `gt2w`, `pending`) наружу не показываем — переводим через те же словари, что
 * и остальной экран.
 */
function describeEvent(
  event: NonNullable<WorkStatusDetail["events"]>[number],
  t: (key: string, opts?: Record<string, unknown>) => string,
  locale: string,
): string {
  const status = (v: string | null | undefined): string =>
    v == null ? "—" : t(`workStatus.status.${v}`);
  const forecast = (v: string | null | undefined): string =>
    v == null ? "—" : t(`workStatus.forecast.${v}`);
  switch (event.kind) {
    case "status":
      return t("workStatus.panel.event.status", {
        from: status(event.from_value),
        to: status(event.to_value),
      });
    case "forecast":
      return t("workStatus.panel.event.forecast", {
        from: forecast(event.from_value),
        to: forecast(event.to_value),
      });
    case "due_date": {
      // Без своей ветки событие уходило в `default` и печаталось пустой
      // строкой — поймано живым смоуком.
      const day = (v: string | null | undefined): string =>
        v == null ? "—" : formatDate(v, locale);
      return t("workStatus.panel.event.dueDate", {
        from: day(event.from_value),
        to: day(event.to_value),
      });
    }
    case "completed_at": {
      // Даты события хранятся ISO — наружу отдаём в том же виде, что и
      // остальные даты панели, иначе журнал говорит на машинном языке.
      const day = (v: string | null | undefined): string =>
        v == null ? "—" : formatDate(v, locale);
      return t("workStatus.panel.event.completedAt", {
        from: day(event.from_value),
        to: day(event.to_value),
      });
    }
    case "checkpoint":
      return t(`workStatus.panel.event.checkpoint.${event.to_value ?? "pending"}`, {
        title: event.memo ?? "",
      });
    default:
      return event.memo ?? "";
  }
}
